export const classificationTour = {
    module: 'classification',
    title: 'Classification Tour',
    title_vi: 'Hướng dẫn Phân loại',
    description: 'Explore Taxonomies, Labeling, Lifecycle, Review Tasks, Exception Queue and the related workspaces. Read the screens and controls without submitting business changes.',
    description_vi: 'Tìm hiểu Phân loại, Gán nhãn, Vòng đời, Nhiệm vụ xem xét, Hàng đợi ngoại lệ và các không gian làm việc liên quan. Xem màn hình và điều khiển mà không gửi thay đổi nghiệp vụ.',
    estimated_duration_seconds: 289,
    revision: '2026-10-26.2',
    steps: [
        {
            step_id: 'explore_classification_taxonomies',
            navigate_to: '/admin/classification/taxonomies',
            target: '[data-testid="classification-taxonomies-page"] .dm-filters',
            wait_for: '[data-testid="classification-taxonomies-page"][data-tour-ready="true"]',
            title: 'Taxonomies',
            title_vi: 'Phân loại',
            description: 'Filter taxonomies by draft, active or deprecated state before choosing a classification scheme. A taxonomy defines the levels that labels can use; its version and scope tell you which scheme you are reviewing. Activating or retiring a scheme is a separate write decision.',
            description_vi: 'Lọc hệ thống phân loại theo trạng thái nháp, hoạt động hoặc không còn áp dụng trước khi chọn sơ đồ phân loại. Hệ thống phân loại xác định các cấp độ dùng cho nhãn; phiên bản và phạm vi cho biết sơ đồ đang được rà soát. Kích hoạt hoặc ngừng dùng sơ đồ là quyết định ghi riêng.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'feature_taxonomy_register',
            navigate_to: '/admin/classification/taxonomies',
            target: '[data-testid="classification-taxonomies-page"] > .dm-table',
            wait_for: '[data-testid="classification-taxonomies-page"][data-tour-ready="true"]',
            title: 'Scope, Levels and Version History',
            title_vi: 'Phạm vi, cấp độ và lịch sử phiên bản',
            description: 'Compare taxonomy scope, status and current version. A record’s level controls show its classification levels, and version-history controls show earlier versions when available. Use those details to understand the scheme before applying labels elsewhere. An empty register still displays the table structure and absence message.',
            description_vi: 'So sánh phạm vi, trạng thái và phiên bản hiện tại của hệ thống phân loại. Điều khiển cấp độ của bản ghi hiển thị các cấp phân loại; điều khiển lịch sử hiển thị phiên bản trước khi có. Dùng chi tiết đó để hiểu sơ đồ trước khi gán nhãn ở nơi khác. Sổ trống vẫn hiển thị cấu trúc bảng và thông báo không có dữ liệu.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'feature_taxonomy_new',
            navigate_to: '/admin/classification/taxonomies',
            target: '[data-testid="create-taxonomy"]',
            wait_for: '[data-testid="classification-taxonomies-page"][data-tour-ready="true"]',
            title: 'Define a Classification Scheme',
            title_vi: 'Định nghĩa sơ đồ phân loại',
            description: 'New Taxonomy opens a draft for the scheme’s name, description and scope. Classification levels are managed against a taxonomy, rather than typed independently on every record. Check whether an existing scheme already serves the purpose before creating another. This step neither creates a scheme nor seeds defaults.',
            description_vi: 'Hệ thống phân loại mới mở bản nháp để nhập tên, mô tả và phạm vi. Các cấp phân loại được quản lý theo hệ thống, thay vì nhập độc lập trên từng bản ghi. Kiểm tra sơ đồ hiện có đã đáp ứng mục đích hay chưa trước khi tạo thêm. Bước này không tạo sơ đồ hoặc khởi tạo giá trị mặc định.',
            position: 'bottom',
            duration_ms: 19000,
            required_permissions: ['classification:write'],
        },
        {
            step_id: 'explore_classification_labeling',
            navigate_to: '/admin/classification/labeling',
            target: '[data-testid="classification-labeling-page"] .dm-filters',
            wait_for: '[data-testid="classification-labeling-page"][data-tour-ready="true"]',
            title: 'Labeling',
            title_vi: 'Gán nhãn',
            description: 'Compare the object and type, taxonomy, level, label status, rationale and review date.',
            description_vi: 'So sánh đối tượng và loại, hệ thống phân loại, cấp, trạng thái nhãn, lý do và ngày rà soát.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_classification_labeling_register',
            navigate_to: '/admin/classification/labeling',
            target: '[data-tour-register="explore_classification_labeling_register"]',
            wait_for: '[data-testid="classification-labeling-page"][data-tour-ready="true"]',
            title: 'Labeling — record decisions',
            title_vi: 'Gán nhãn — quyết định theo bản ghi',
            description: 'Proposed or under-review labels are not approved decisions. Stale-label and parent-level conflict sections appear when there are records to inspect; investigate those differences before changing or approving labels.',
            description_vi: 'Nhãn đề xuất hoặc đang rà soát chưa phải quyết định đã phê duyệt. Phần nhãn quá hạn và xung đột với cấp cha xuất hiện khi có bản ghi; cần xem khác biệt trước khi sửa hoặc phê duyệt.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_classification_lifecycle',
            navigate_to: '/admin/classification/lifecycle',
            target: '[data-testid="classification-lifecycle-page"] .dm-filters',
            wait_for: '[data-testid="classification-lifecycle-page"][data-tour-ready="true"]',
            title: 'Lifecycle',
            title_vi: 'Vòng đời',
            description: 'Read current stage, retention period in days, retention expiry, responsible party and hold reason together.',
            description_vi: 'Đọc cùng nhau giai đoạn hiện tại, số ngày lưu giữ, ngày hết hạn, bên phụ trách và lý do tạm giữ.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_classification_lifecycle_register',
            navigate_to: '/admin/classification/lifecycle',
            target: '[data-tour-register="explore_classification_lifecycle_register"]',
            wait_for: '[data-testid="classification-lifecycle-page"][data-tour-ready="true"]',
            title: 'Lifecycle — record decisions',
            title_vi: 'Vòng đời — quyết định theo bản ghi',
            description: 'A hold restricts disposal; it is not another lifecycle stage. The event timeline, when opened, shows who moved a record and why; viewing a record neither releases a hold nor authorises disposal.',
            description_vi: 'Tạm giữ hạn chế tiêu hủy, không phải giai đoạn vòng đời khác. Khi mở, dòng thời gian cho biết ai chuyển bản ghi và vì sao; xem bản ghi không gỡ tạm giữ hoặc cho phép tiêu hủy.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_classification_review_tasks',
            navigate_to: '/admin/classification/review-tasks',
            target: '[data-testid="classification-review-tasks-page"] .dm-filters',
            wait_for: '[data-testid="classification-review-tasks-page"][data-tour-ready="true"]',
            title: 'Review Tasks',
            title_vi: 'Nhiệm vụ xem xét',
            description: 'Use system and status to locate label-review tasks, then compare the recorded resolution and notes.',
            description_vi: 'Dùng hệ thống và trạng thái để tìm nhiệm vụ rà soát nhãn, rồi so sánh kết quả xử lý và ghi chú.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_classification_review_tasks_register',
            navigate_to: '/admin/classification/review-tasks',
            target: '[data-tour-register="explore_classification_review_tasks_register"]',
            wait_for: '[data-testid="classification-review-tasks-page"][data-tour-ready="true"]',
            title: 'Review Tasks — record decisions',
            title_vi: 'Nhiệm vụ xem xét — quyết định theo bản ghi',
            description: 'A completed status and its decision need to be read together. Review-write access is separate from reading, so this walkthrough does not approve, reject or route a task.',
            description_vi: 'Cần đọc trạng thái hoàn thành cùng quyết định đã ghi. Quyền cập nhật rà soát tách khỏi quyền đọc, nên hướng dẫn không phê duyệt, từ chối hoặc chuyển nhiệm vụ.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_classification_exceptions',
            navigate_to: '/admin/classification/exceptions',
            target: '[data-testid="classification-exceptions-page"] .dm-filters',
            wait_for: '[data-testid="classification-exceptions-page"][data-tour-ready="true"]',
            title: 'Exception Queue',
            title_vi: 'Hàng đợi ngoại lệ',
            description: 'Read the affected system, severity, description and status before considering resolution.',
            description_vi: 'Đọc hệ thống bị ảnh hưởng, mức độ nghiêm trọng, mô tả và trạng thái trước khi cân nhắc xử lý.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_classification_exceptions_register',
            navigate_to: '/admin/classification/exceptions',
            target: '[data-tour-register="explore_classification_exceptions_register"]',
            wait_for: '[data-testid="classification-exceptions-page"][data-tour-ready="true"]',
            title: 'Exception Queue — record decisions',
            title_vi: 'Hàng đợi ngoại lệ — quyết định theo bản ghi',
            description: 'The recorded resolution outcome explains the decision; a closed queue item alone cannot explain whether a conflict was corrected or accepted. Inspect the source record before proposing a change.',
            description_vi: 'Kết quả giải quyết ghi nhận quyết định; chỉ mục đã đóng chưa cho biết xung đột được sửa hay chấp nhận. Xem bản ghi gốc trước khi đề xuất thay đổi.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_classification_gaps',
            navigate_to: '/admin/classification/gaps',
            target: '[data-testid="classification-gaps-page"] .dm-filters',
            wait_for: '[data-testid="classification-gaps-page"][data-tour-ready="true"]',
            title: 'Gap Report',
            title_vi: 'Báo cáo lỗi',
            description: 'Compare gap type and severity with the related object and status.',
            description_vi: 'So sánh loại khoảng trống và mức độ nghiêm trọng với đối tượng liên quan và trạng thái.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_classification_gaps_register',
            navigate_to: '/admin/classification/gaps',
            target: '[data-tour-register="explore_classification_gaps_register"]',
            wait_for: '[data-testid="classification-gaps-page"][data-tour-ready="true"]',
            title: 'Gap Report — record decisions',
            title_vi: 'Báo cáo lỗi — quyết định theo bản ghi',
            description: 'Summary counts and the filtered register answer different questions; a zero filtered result is not proof that every asset is classified. Follow the affected object before deciding whether to assign remediation or record an exception.',
            description_vi: 'Tổng hợp và sổ đã lọc trả lời câu hỏi khác nhau; kết quả lọc bằng không không chứng minh mọi tài sản đã được phân loại. Theo đối tượng bị ảnh hưởng trước khi giao khắc phục hoặc ghi ngoại lệ.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_classification_enterprise_map',
            navigate_to: '/admin/classification/enterprise-map',
            target: '[data-tour-feature="tour-classification-enterprise-map"]',
            wait_for: '[data-testid="classification-enterprise-map-page"][data-tour-ready="true"]',
            title: 'Enterprise Data Map',
            title_vi: 'Bản đồ dữ liệu doanh nghiệp',
            description: 'Compare each system’s labels, status and last update. This map summarizes recorded classification; it does not establish that discovery covered every source. Open the appropriate labeling or review workspace to investigate an incomplete or outdated classification.',
            description_vi: 'So sánh nhãn, trạng thái và lần cập nhật cuối của từng hệ thống. Bản đồ tóm tắt phân loại đã ghi, không xác nhận khám phá đã bao phủ mọi nguồn. Mở không gian gán nhãn hoặc rà soát phù hợp để điều tra phân loại thiếu hoặc cũ.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_classification_discovery_cycles',
            navigate_to: '/admin/classification/discovery-cycles',
            target: '[data-tour-feature="tour-classification-discovery-cycles"]',
            wait_for: '[data-testid="classification-discovery-cycles-page"][data-tour-ready="true"]',
            title: 'Discovery Cycles',
            title_vi: 'Chu kỳ Khám phá',
            description: 'Read cycle scope, status and schedule before examining any available delta results. Deltas distinguish previous and current classifications; the schedule is not evidence that a run completed. These classification cycles are separate from the operational scan cycles in Discovery.',
            description_vi: 'Đọc phạm vi, trạng thái và lịch của kỳ trước khi xem kết quả chênh lệch có sẵn. Chênh lệch phân biệt phân loại trước và hiện tại; lịch không chứng minh một lần chạy đã xong. Kỳ phân loại tách khỏi chu kỳ quét vận hành trong Khám phá.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_classification_exports',
            navigate_to: '/admin/classification/exports',
            target: '[data-tour-feature="tour-classification-exports"]',
            wait_for: '[data-testid="classification-exports-page"][data-tour-ready="true"]',
            title: 'Exports',
            title_vi: 'Xuất dữ liệu',
            description: 'The CSV and PDF sections offer different recorded classification views. Choose the register and intended audience before exporting; a file preserves the available records, not a new approval or complete-discovery finding. This walkthrough triggers no export or download.',
            description_vi: 'Các phần CSV và PDF cung cấp các góc nhìn khác nhau về phân loại đã ghi. Chọn sổ và người nhận dự kiến trước khi xuất; tệp lưu dữ liệu có sẵn, không tạo phê duyệt hoặc kết luận khám phá đầy đủ. Hướng dẫn không xuất hoặc tải tệp.',
            position: 'bottom',
            duration_ms: 18000,
        },
    ],
};
//# sourceMappingURL=classification.js.map